import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MicrophoneIcon,
  CalendarIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { Patient } from '../../types/patient';
import { ClinicalCard, QuickAction } from './shared';
import { useAuth } from '../../hooks/useAuth';
import { useOrganizationStore } from '../../stores/organizationStore';
import QuickRecordingModal from '../QuickRecordingModal';

/**
 * QuickActions - Panel with common clinical workflow actions
 * Provides one-click access to frequent tasks doctors perform
 */
interface QuickActionsProps {
  patient: Patient;
  onTranscriptView?: (appointmentId: string) => void;
  className?: string;
}

const QuickActions: React.FC<QuickActionsProps> = ({
  patient,
  onTranscriptView,
  className = ''
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { operationMode } = useOrganizationStore();
  const [isQuickRecordingOpen, setIsQuickRecordingOpen] = useState(false);
  const [isLoading, setIsLoading] = useState<string | null>(null);

  // Booking and editing patients are standalone-only and limited to admins and operators;
  // the server rejects them for anyone else, so other users do not see them.
  const canEdit = operationMode === 'standalone' &&
    (user?.role === 'admin' || user?.role === 'operator');
  // Who may record comes from the server (AMP-136): null until loaded, false hides the action.
  const canRecord = useOrganizationStore((s) => s.canRecord);
  const isProvider = user?.role === 'admin' || user?.role === 'operator';

  const handleQuickAction = async (actionType: string, action: () => void) => {
    setIsLoading(actionType);
    try {
      await action();
    } finally {
      setIsLoading(null);
    }
  };

  const actions = [
    {
      id: 'record',
      title: 'Start New Recording',
      description: 'Begin audio transcription for current appointment',
      icon: <MicrophoneIcon className="w-5 h-5" />,
      onClick: () => setIsQuickRecordingOpen(true),
      variant: 'primary' as const,
      disabled: canRecord !== true,
      show: isProvider && canRecord !== false
    },
    {
      id: 'schedule',
      title: 'Schedule Appointment',
      description: 'Book new appointment for this patient',
      icon: <CalendarIcon className="w-5 h-5" />,
      onClick: () => navigate(`/appointments/new?patientId=${patient.id}`),
      variant: 'secondary' as const,
      disabled: false,
      show: canEdit
    },
    {
      id: 'edit',
      title: 'Edit Patient Info',
      description: 'Update demographics and medical history',
      icon: <PencilIcon className="w-5 h-5" />,
      onClick: () => navigate(`/patients/${patient.id}/edit`),
      variant: 'secondary' as const,
      disabled: false,
      show: canEdit
    }
  ];

  const visibleActions = actions.filter(action => action.show);

  return (
    <>
      <ClinicalCard
        title="Quick Actions"
        icon={<MicrophoneIcon className="w-5 h-5" />}
        className={className}
        size="md"
      >
        <div className="space-y-3">
          {visibleActions.map((action) => (
            <QuickAction
              key={action.id}
              title={action.title}
              description={action.description}
              icon={action.icon}
              onClick={() => handleQuickAction(action.id, action.onClick)}
              variant={action.variant}
              disabled={action.disabled}
              loading={isLoading === action.id}
            />
          ))}
          {visibleActions.length === 0 && (
            <p className="text-sm text-gray-500">No actions are available for your role.</p>
          )}
        </div>

        {/* Permissions Notice */}
        {!canEdit && operationMode === 'connected' && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2"></div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  <strong>Connected Mode:</strong> Some actions are read-only due to EHR integration.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Patient Status Quick Info */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Record updated:</span>
              <div className="font-medium text-gray-900">
                {patient.updatedAt ? new Date(patient.updatedAt).toLocaleDateString() : 'Unknown'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>
              <div className={`font-medium ${patient.isActive ? 'text-green-600' : 'text-gray-600'}`}>
                {patient.isActive ? 'Active' : 'Inactive'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">Contacts:</span>
              <div className="font-medium text-gray-900">
                {patient.emergencyContacts?.length || 0} emergency
              </div>
            </div>
            <div>
              <span className="text-gray-500">Language:</span>
              <div className="font-medium text-gray-900 uppercase">
                {patient.demographics.language || 'EN'}
              </div>
            </div>
          </div>
        </div>
      </ClinicalCard>

      {/* Quick Recording Modal */}
      <QuickRecordingModal
        isOpen={isQuickRecordingOpen}
        onClose={() => setIsQuickRecordingOpen(false)}
      />
    </>
  );
};

export default QuickActions;